import { MeetingLobby } from './MeetingLobby';
import { MeetingRoom } from './MeetingRoom';
import type { MeetingLeaveOptions } from './MeetingControlBar';
import type { MeetingJoinSettings, MeetingRoomRole } from './types';

export type MeetingSessionShellProps = {
  token: string;
  serverUrl: string;
  /** LiveKit room id. Same-room remints must not remount MeetingRoom. */
  roomName?: string;
  participantRole: MeetingRoomRole;
  lobbyDone: boolean;
  joinSettings: MeetingJoinSettings;
  onLobbyJoin: (settings: MeetingJoinSettings) => void;
  onLeave?: () => void;
  onRejoin?: () => void;
  onConnected?: () => void;
  expectDisconnect?: boolean;
  children: React.ReactNode;
} & MeetingLeaveOptions;

/**
 * Orchestrates lobby then in-room experience. Lobby state is owned by the parent
 * so device choices survive token refresh on rejoin.
 *
 * Key MeetingRoom by room, not JWT. start-session / expiry remints mint a new
 * token for the same room; key={token} unmounts LiveKit, disconnects, and the
 * student sees Connection lost. Cross-room remints (preflight → exam) change
 * roomName and remount on purpose.
 */
export function MeetingSessionShell({
  token,
  serverUrl,
  roomName,
  participantRole,
  lobbyDone,
  joinSettings,
  onLobbyJoin,
  onLeave,
  onRejoin,
  onConnected,
  expectDisconnect = false,
  skipLeaveConfirm,
  beforeLeave,
  children,
}: MeetingSessionShellProps) {
  if (!lobbyDone) {
    return <MeetingLobby onJoin={onLobbyJoin} />;
  }

  return (
    <MeetingRoom
      key={roomName || 'meeting-session'}
      token={token}
      serverUrl={serverUrl}
      participantRole={participantRole}
      joinSettings={joinSettings}
      onLeave={onLeave}
      onRejoin={onRejoin}
      onConnected={onConnected}
      expectDisconnect={expectDisconnect}
      skipLeaveConfirm={skipLeaveConfirm}
      beforeLeave={beforeLeave}
    >
      {children}
    </MeetingRoom>
  );
}
